import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import { Grid } from '@mui/material';
import {
  CancelReasons,
  CustomFieldsMapper,
  CustomFields as CustomFieldsValueObject,
} from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import iconReportPlace from '../../../../../../../assets/img/icon-report-place.svg';
import iconReportCancelation from '../../../../../../../assets/img/icon-reportReasonCancel.svg';
import BackArrowIcon from '../../../../../../../assets/svg/BackArrowIcon';
import CommentsIcon from '../../../../../../../assets/svg/CommentsIcon';
import ImageIcon from '../../../../../../../assets/svg/ImageIcon';
import { Modal } from '../../../../../../../components/Modal';
import * as routing from '../../../../../../../constants/Routing';
import { STOP_STATUS } from '../../../../../../../constants/Stops';
import { DriverProjectFieldElement } from '../../../../../../../domain/driversProject/DriversProject';
import { ImageUploadedProps } from '../../../../../../../domain/report/StopReportCreate';
import { Route } from '../../../../../../../domain/route/Route';
import { Stop } from '../../../../../../../domain/stop/Stop';
import { intl } from '../../../../../../../helpers/IntGlobalProvider';
import Button from '../../../../../../../react-components/Button';
import InputText from '../../../../../../../react-components/InputText';
import { Text } from '../../../../../../../react-components/Text';
import { useLocationContext } from '../../../../../context/locationContext/context';
import { useRetrieveVehicle } from '../../../../../hooks/useRetrieveVehicle';
import { useRouteContext } from '../../../../context/context';
import { useCreateStopReport } from '../../../../hooks/useCreateStopReport';
import { StopPillInfo } from '../../../StopPillInfo';
import { ReportFooter, ReportFooterType, StopReportFooter } from '../StopReportFooter';
import { CameraMenu, ServiceImageCameraMenu } from '../components/CameraMenu';
import { ReportFields } from '../components/ReportFields';
import { ReportMap } from '../components/ReportMap';
import { StopReportContainer } from '../components/StopReportContainer';

interface StopCancelProps {
  stop: Stop;
  selectedRoute: Route;
  onClose: (stopId: string) => void;
  onCanceled: (stopId: string | undefined) => void;
}

export const StopCancel = ({ stop, selectedRoute, onClose, onCanceled }: StopCancelProps) => {
  const navigate = useNavigate();
  const { data: vehicleData } = useRetrieveVehicle();

  const { locationState } = useLocationContext();

  const footer = React.useRef<HTMLDivElement>(null);
  const header = React.useRef<HTMLDivElement>(null);
  const [offsetContentHeight, setOffsetContentHeight] = useState<number>(window.innerHeight);

  const { run: createReport, success, isLoading } = useCreateStopReport();
  const [customFields, setCustomFields] = useState<CustomFieldsValueObject>(CustomFieldsValueObject.create({}));

  const [cancelReason, setCancelReason] = React.useState<CancelReasons | undefined>(undefined);
  const [images, setImages] = useState<ImageUploadedProps[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [comments, setComments] = React.useState<string | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showMandatoryFields, setShowMandatoryFields] = React.useState<boolean>(false); //It can be use to show mandatory fields
  const [enableCamera, setEnableCamera] = useState<boolean>(false);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);
  const [cancelDeliveryDialogOpen, setCancelDeliveryDialogOpen] = useState<boolean>(false);
  const [cancelPickUPDialogOpen, setCancelPickUPDialogOpen] = useState<boolean>(false);

  const isDriverPositionMandatory = vehicleData?.project.fields.configuration?.includes(`driver_position`);

  const { routeState } = useRouteContext();

  const requiredCustomFields = vehicleData?.project
    ? vehicleData.project.fields.stop_canceled_fields.filter((field) => field.is_required && field.is_custom_field)
    : [];
  const requiredNotCustomFields = vehicleData?.project
    ? vehicleData.project.fields.stop_canceled_fields.filter((field) => field.is_required && !field.is_custom_field)
    : [];

  const checkRequiredFields = (field_name: string) => {
    if (field_name === `cancel_reason`) {
      return cancelReason !== undefined;
    }
    if (field_name === `comments`) {
      return comments && comments !== ``;
    }
    if (field_name === `images`) {
      return images.length > 0;
    }
    return false;
  };

  const remainingRequiredCustomFields = requiredCustomFields.filter((field) => {
    return customFields.value[field.id] === undefined;
  });

  const remainingRequiredNotCustomFields = requiredNotCustomFields.filter((field) => {
    return !checkRequiredFields(field.id);
  });

  useEffect(() => {
    if (
      stop.report ||
      (vehicleData?.project.fields.configuration.includes(`stop_duration`) && routeState.startedReportStop !== stop.id)
    ) {
      navigate(routing.PRIVATE_ROUTES.STOP_DETAILS.replace(`:routeId`, selectedRoute.id).replace(`:stopId`, stop.id));
    }

    const footerHeight = footer.current?.offsetHeight || 0;
    const headerHeight = header.current?.offsetHeight || 0;
    setOffsetContentHeight(footerHeight + headerHeight);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      const nextStopId = selectedRoute.stops.find(
        (stop_find) => stop_find.status === STOP_STATUS.pending && stop.id !== stop_find.id
      )?.id;
      onCanceled(nextStopId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  const cancelReasons = [
    {
      translationId: `driver.stop.reportv2.cancel_reason.nobody_at_home`,
      tag: `nobody` as CancelReasons,
    },
    {
      translationId: `driver.stop.reportv2.cancel_reason.wrong_address`,
      tag: `wrong_address` as CancelReasons,
    },
    {
      translationId: `driver.stop.reportv2.cancel_reason.missing_information`,
      tag: `missing_information` as CancelReasons,
    },
    {
      translationId: `driver.stop.reportv2.cancel_reason.other_reasons`,
      tag: `other` as CancelReasons,
    },
  ];

  const addData = (fieldName: string, fieldValue: number | string | boolean | number[]) => {
    if (customFields) {
      customFields.update({ [fieldName]: fieldValue });
      setCustomFields(CustomFieldsValueObject.create(CustomFieldsMapper.toDTO(customFields)));
    } else {
      setCustomFields(CustomFieldsValueObject.create({ [fieldName]: fieldValue }));
    }
  };

  const checkValues = () => {
    const hasComments = comments && comments !== ``;
    const hasCancelReason = cancelReason;
    const hasCustomFields = Object.keys(customFields.value).length > 0;
    const hasImage = images.length > 0;
    return hasComments || hasCancelReason || hasCustomFields || hasImage;
  };

  const clearAllSets = () => {
    setCancelReason(undefined);
    setComments(undefined);
    setImages([]);
    setEnableCamera(false);
    setSelectedIndex(-1);
    setCustomFields(CustomFieldsValueObject.create({}));
    setShowCloseModal(false);
    setCancelDeliveryDialogOpen(false);
    setCancelPickUPDialogOpen(false);
  };

  const handleImagesChange = (newImages: ImageUploadedProps[]) => {
    setImages(newImages);
  };

  if (enableCamera) {
    return (
      <CameraMenu
        enableGallery={vehicleData?.project.gallery_enabled}
        selectedIndexInput={selectedIndex}
        inputImages={images}
        outputImages={handleImagesChange}
        closeCamera={() => setEnableCamera(false)}
        limitImages={5}
      />
    );
  }

  /**
   * Shows a dialog when stop is a pickup. If the delivery status is pending, asks user if it should be also canceled.
   */
  const onCancel = async () => {
    if (
      remainingRequiredCustomFields.length > 0 ||
      remainingRequiredNotCustomFields.length > 0 ||
      (isDriverPositionMandatory && locationState.currentPosition === undefined)
    ) {
      setShowMandatoryFields(true);
      return;
    }

    if (stop.type === `pickup`) {
      const deliveryFound = selectedRoute.getDeliveryByPickupId(stop.id);
      if (deliveryFound) {
        setCancelDeliveryDialogOpen(true);
        return;
      }
    }
    if (stop.type === `delivery` && stop.pickup_id) {
      const pickupFound = selectedRoute.getStopById(stop.pickup_id);
      if (pickupFound && pickupFound.status === STOP_STATUS.pending) {
        setCancelPickUPDialogOpen(true);
        return;
      }
    }
    if (!isLoading && !success && stop.status === STOP_STATUS.pending) {
      cancelStop(stop);
    }
  };

  /**
   * Cancels the pickup and its delivery.
   */
  const cancelPickupAndDelivery = async () => {
    setCancelDeliveryDialogOpen(false);

    // Cancels the pickup
    if (!isLoading && !success && stop.status === STOP_STATUS.pending) {
      await cancelStop(stop);

      // Cancels the delivery if found.
      const deliveryFound = selectedRoute.getDeliveryByPickupId(stop.id);
      if (deliveryFound && deliveryFound.status === STOP_STATUS.pending) {
        cancelStop(deliveryFound);
      }
    }
  };

  /**
   * Cancels the delivery and its Pickup.
   */
  const cancelDeliveryAndPickup = async () => {
    setCancelPickUPDialogOpen(false);

    // Cancels the pickup
    if (!isLoading && !success && stop.status === STOP_STATUS.pending) {
      await cancelStop(stop);

      // Cancels the delivery if found.
      if (stop.pickup_id) {
        const pickupFound = selectedRoute.getStopById(stop.pickup_id);
        if (pickupFound && pickupFound.status === STOP_STATUS.pending) {
          cancelStop(pickupFound);
        }
      }
    }
  };

  const cancelStop = async (stopToCancel: Stop) => {
    createReport({
      status: `canceled`,
      stop: stopToCancel,
      reportData: {
        comments: comments && comments !== `` ? comments : undefined,
        customFields,
        cancelReason: cancelReason as CancelReasons,
      },
      images: images.filter((image) => !!image.data),
    });
  };

  if (vehicleData?.project.fields.stop_canceled_fields.length === 0) {
    if (!isLoading && !success && stop.status === STOP_STATUS.pending) {
      cancelStop(stop);
    }
    return null;
  }

  return (
    <>
      <Modal
        onClose={() => {
          setCancelPickUPDialogOpen(false);
        }}
        open={cancelPickUPDialogOpen}
        title={{
          intlId: `driver.stop.report.cancel_pickup_on_cancel_delivery_dialog.title`,
        }}
        subtitle={{
          intlId: `driver.stop.report.cancel_pickup_on_cancel_delivery_dialog.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.report.cancel_pickup_on_cancel_delivery_dialog.cancel`,
            onClick: () => {
              if (!isLoading && !success && stop.status === STOP_STATUS.pending) {
                cancelStop(stop);
              }
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `accept`,
            onClick: () => {
              cancelDeliveryAndPickup();
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        onClose={() => setCancelDeliveryDialogOpen(false)}
        open={cancelDeliveryDialogOpen}
        title={{
          intlId: `driver.stop.report.cancel_delivery_on_cancel_pickup_dialog.title`,
        }}
        subtitle={{
          intlId: `driver.stop.report.cancel_delivery_on_cancel_pickup_dialog.info`,
        }}
        buttons={[
          {
            intlId: `driver.stop.report.cancel_delivery_on_cancel_pickup_dialog.cancel`,
            onClick: () => {
              if (!isLoading && !success && stop.status === STOP_STATUS.pending) {
                cancelStop(stop);
              }
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `accept`,
            onClick: () => {
              cancelPickupAndDelivery();
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Modal
        open={showCloseModal}
        title={{
          intlId: `drivers.stop.report.modal.title`,
        }}
        subtitle={{
          intlId: `drivers.stop.report.modal.subtitle`,
        }}
        buttons={[
          {
            intlId: `drivers.stop.report.modal.leave`,
            onClick: () => {
              setShowCloseModal(false);
              onClose(stop.id);
              clearAllSets();
            },
            variant: `text`,
            style: {
              border: `none`,
            },
          },
          {
            intlId: `cancel`,
            onClick: () => {
              setShowCloseModal(false);
            },
          },
        ]}
        children={<VSpacer medium />}
      />
      <Grid
        container
        style={{
          position: `absolute`,
          inset: 0,
          display: `flex`,
          alignContent: `flex-start`,
          backgroundColor: RoutalPalette.white,
          zIndex: 1003,
        }}
      >
        {/* HEADER */}
        <Grid
          ref={header}
          style={{
            display: `flex`,
            flexDirection: `row`,
            flexWrap: `nowrap`,
            width: `100%`,
            height: `fit-content`,
            justifyContent: `space-between`,
            alignItems: `center`,
            padding: `10px 20px 0px 20px`,
            borderBottom: `1px solid ${RoutalPalette.neutral10}`,
          }}
        >
          <StopPillInfo
            label={stop.location?.label}
            subLabel={stop.label}
            id={stop.id}
            stopOrder={stop.order + 1}
            maxStops={selectedRoute.stops.length}
          />
          <BackArrowIcon
            style={{
              color: RoutalPalette.secondary40,
              height: `28px`,
              width: `28px`,
              fontSize: `25px`,
              cursor: `pointer`,
            }}
            onClick={(e: any) => {
              if (!isLoading) {
                if (checkValues()) {
                  setShowCloseModal(true);
                } else {
                  onClose(stop.id);
                }
              }
            }}
          />
        </Grid>

        {/* CONTENT */}
        <Grid
          container
          style={{
            height: `calc(100% - ${offsetContentHeight}px)`,
            width: `100%`,
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="space-between"
            display="block"
            wrap="nowrap"
            style={{
              // minHeight: `${window.innerHeight - 130}px`,
              height: `100%`,
              pointerEvents: isLoading ? `none` : undefined,
              flexDirection: `column`,
              // flexWrap: `nowrap`,
              overflowY: `auto`,
              overflowX: `hidden`,
              padding: `8px 20px 8px 20px`,
            }}
          >
            <VSpacer medium />
            <StopReportContainer
              textId="drivers.stop.report.location"
              showMandatory={showMandatoryFields}
              isMandatory={vehicleData?.project.fields.configuration.includes(`driver_position`)}
              childrenImage={<img alt="Report" src={iconReportPlace} style={{ height: `15px`, width: `20px` }} />}
              hasValue={true}
            >
              <ReportMap
                selectedRoute={selectedRoute}
                showMandatory={isDriverPositionMandatory && locationState.currentPosition === undefined}
                hasValue={locationState.currentPosition !== undefined}
              />
            </StopReportContainer>
            <VSpacer medium />

            {vehicleData?.project.fields?.stop_canceled_fields?.map(
              (field: DriverProjectFieldElement, index: number) => {
                if (field.is_custom_field) {
                  return (
                    <ReportFields
                      customFields={customFields}
                      addData={addData}
                      fieldElement={field}
                      position={index}
                      showMandatory={showMandatoryFields}
                    />
                  );
                }

                switch (field.id) {
                  case `cancel_reason`:
                    return (
                      <>
                        <StopReportContainer
                          textId="drivers.stop.report.cancel_reasons"
                          showMandatory={showMandatoryFields}
                          isMandatory={field.is_required}
                          hasValue={cancelReason !== undefined}
                          childrenImage={
                            <img
                              src={iconReportCancelation}
                              alt="Report"
                              style={{ height: `15px`, width: `20px`, color: RoutalPalette.secondary00 }}
                            />
                          }
                        >
                          <Grid item>
                            {cancelReasons.map((cancelElement, index, array) => (
                              <Button
                                key={cancelElement.tag}
                                onClick={() => {
                                  setCancelReason(cancelElement.tag as CancelReasons);
                                }}
                                style={{
                                  height: `30px`,
                                  fontSize: `13px`,
                                  marginBottom: index === array.length - 1 ? `0px` : `10px`,
                                  border:
                                    cancelElement.tag === cancelReason
                                      ? `1px solid ${RoutalPalette.primary40}`
                                      : `1px solid ${RoutalPalette.grey7}`,
                                  fontWeight: `normal`,
                                  color:
                                    cancelElement.tag === cancelReason
                                      ? RoutalPalette.white
                                      : `#${RoutalPalette.grey7}`,
                                  backgroundColor:
                                    cancelElement.tag === cancelReason ? RoutalPalette.primary40 : `transparent`,
                                }}
                              >
                                <Text
                                  variant="label"
                                  intlId={cancelElement.translationId}
                                  style={{
                                    fontSize: `13px`,
                                    color: cancelElement.tag === cancelReason ? RoutalPalette.white : undefined,
                                  }}
                                />
                              </Button>
                            ))}
                          </Grid>
                        </StopReportContainer>
                        <VSpacer medium />
                      </>
                    );
                  case `images`:
                    return (
                      <>
                        <StopReportContainer
                          textId="drivers.stop.report.images"
                          showMandatory={showMandatoryFields}
                          isMandatory={field.is_required}
                          childrenImage={
                            <ImageIcon style={{ height: `15px`, width: `20px`, color: RoutalPalette.secondary00 }} />
                          }
                          hasValue={images.length > 0}
                        >
                          {images.length > 0 && (
                            <>
                              <Grid
                                container
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                style={{
                                  gap: `10px`,
                                  display: `flex`,
                                  flexWrap: `nowrap`,
                                  alignItems: `center`,
                                  overflowX: `auto`,
                                  overflowY: `hidden`,
                                  height: `100px`,
                                }}
                              >
                                {images.map((image: ImageUploadedProps, index: number) => {
                                  return (
                                    <div key={image.tempId} style={{ height: `100px` }}>
                                      <ServiceImageCameraMenu
                                        imageData={image.data}
                                        style={{ height: `100px`, aspectRatio: `9/16`, objectFit: `cover` }}
                                        onClick={() => {
                                          setSelectedIndex(index);
                                          setEnableCamera(true);
                                        }}
                                      />
                                    </div>
                                  );
                                })}
                              </Grid>
                              <VSpacer medium />
                            </>
                          )}
                          <Grid
                            item
                            container
                            direction="row"
                            alignItems="center"
                            style={{
                              height: `50px`,
                              backgroundColor: RoutalPalette.primary40,
                              borderColor: RoutalPalette.primary40,
                              display: `flex`,
                              flexDirection: `row`,
                              flexWrap: `nowrap`,
                              justifyContent: `center`,
                              alignItems: `center`,
                              borderRadius: `10px`,
                            }}
                            onClick={(e: any) => {
                              setEnableCamera(!enableCamera);
                            }}
                          >
                            <AddAPhotoOutlinedIcon style={{ fill: RoutalPalette.white }} />
                            <HSpacer small />
                            <Text intlId={`driver.stop.report.add_images`} style={{ color: RoutalPalette.white }} />
                          </Grid>
                        </StopReportContainer>
                        <VSpacer medium />
                      </>
                    );
                  case `comments`:
                    return (
                      <>
                        <StopReportContainer
                          textId="driver.stop.report.tasks.comments"
                          childrenImage={<CommentsIcon alt="iconComment" style={{ height: `15px`, width: `20px` }} />}
                          showMandatory={showMandatoryFields}
                          isMandatory={field.is_required}
                          hasValue={!!comments && comments !== ``}
                        >
                          <InputText
                            multiline
                            placeholder={intl.formatMessage({ id: `driver.stop.report.comments_ph` })}
                            rowsMin={4}
                            fieldValue={comments || ``}
                            onChange={(value) => setComments(value)}
                            variant="outlined"
                            inputContainerStyles={{ width: `100%` }}
                            inputStyles={{
                              width: `100%`,
                              padding: `16.5px 14px`,
                              borderColor: RoutalPalette.grey7,
                            }}
                          />
                        </StopReportContainer>
                        <VSpacer medium />
                      </>
                    );
                  default:
                    return (
                      <ReportFields
                        addData={addData}
                        fieldElement={field}
                        position={index}
                        showMandatory={showMandatoryFields}
                      />
                    );
                }
              }
            )}
          </Grid>
        </Grid>

        {/* FOOTER */}
        <Grid ref={footer} style={{ height: `fit-content`, position: `absolute`, bottom: 0, right: 0, left: 0 }}>
          <StopReportFooter
            onClick={() => {
              onCancel();
            }}
            report={ReportFooter.cancel as ReportFooterType}
            isLoading={isLoading || success}
          />
        </Grid>
      </Grid>
    </>
  );
};
